/**
 * PortalLayout — Marketing-style centered card layout.
 *
 * Header with logo + "License Portal" title, centered content area (max-w-xl),
 * footer with support links. Dark mode toggle, mobile-first responsive.
 */

import { Sun, Moon, Monitor, Shield } from 'lucide-react';
import useLocalStorage from '@shared/hooks/useLocalStorage';

export function PortalLayout({ children }) {
    const [theme, setTheme] = useLocalStorage('portal-theme', 'system');

    function cycleTheme() {
        const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
        setTheme(next);
        applyTheme(next);
    }

    return (
        <div className="flex flex-col min-h-screen">
            {/* ── Header ────────────────────────────────────────── */}
            <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                <div className="max-w-xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        <div>
                            <h1 className="text-lg font-semibold leading-tight">License Portal</h1>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">HForgeX</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={cycleTheme}
                        className="p-2 rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        aria-label={`Theme: ${theme}. Click to change.`}
                    >
                        {theme === 'dark' ? (
                            <Moon className="w-4 h-4" />
                        ) : theme === 'light' ? (
                            <Sun className="w-4 h-4" />
                        ) : (
                            <Monitor className="w-4 h-4" />
                        )}
                    </button>
                </div>
            </header>

            {/* ── Content ───────────────────────────────────────── */}
            <main className="flex-1 w-full max-w-xl mx-auto px-4 sm:px-6 py-8">{children}</main>

            {/* ── Footer ────────────────────────────────────────── */}
            <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                <div className="max-w-xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                    <span>&copy; {new Date().getFullYear()} HForgeX</span>
                    <div className="flex gap-4">
                        <a
                            href="https://hforgex.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                        >
                            Website
                        </a>
                        <a
                            href="https://hforgex.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                        >
                            Privacy
                        </a>
                        <a
                            href="mailto:support@hforgex.com"
                            className="hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                        >
                            Support
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Apply theme to <html> class (mirrors the inline script in index.html)
// ---------------------------------------------------------------------------

function applyTheme(theme) {
    const root = document.documentElement;
    if (theme === 'dark') {
        root.classList.add('dark');
    } else if (theme === 'light') {
        root.classList.remove('dark');
    } else {
        // system
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.toggle('dark', prefersDark);
    }
}
